import { AxiosError } from 'axios';
import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useImperativeHandle, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axiosInstance from 'utils/axiosInterceptors';
import { toastConfig } from 'utils/toastConfig';
import {
  EXPENSE_CATEGORIES,
  EXPENSE_PAYMENT_METHODS,
  EXPENSE_WORKPLACES,
  BranchFunds,
  ExpenseCategory,
  ExpensePaymentMethod,
  ExpenseWorkplace,
  OperatingExpense
} from './expenseOptions';

export type ExpenseFormHandle = {
  submitForm: () => Promise<boolean>;
};

type ExpenseFormProps = {
  branchDomain: string;
  expense: OperatingExpense | null;
  openToken: number;
  refreshKey: number;
  onSaved: () => void;
};

const todayInput = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
};

const apiError = (error: unknown, fallback: string) => {
  if (error instanceof AxiosError) {
    const data = error.response?.data as { errorMessage?: string } | undefined;
    return data?.errorMessage || fallback;
  }
  return fallback;
};

const paymentValue = (method: ExpensePaymentMethod, fundId: number | null) =>
  method === 'Fund' && fundId ? `fund:${fundId}` : method;

const ExpenseForm = React.forwardRef<ExpenseFormHandle, ExpenseFormProps>(
  ({ branchDomain, expense, openToken, refreshKey, onSaved }, ref) => {
    const [expenseDate, setExpenseDate] = useState(todayInput());
    const [workplace, setWorkplace] = useState<ExpenseWorkplace>('Store');
    const [category, setCategory] = useState<ExpenseCategory>('Rent');
    const [payee, setPayee] = useState('');
    const [description, setDescription] = useState('');
    const [amount, setAmount] = useState('');
    const [paymentMethod, setPaymentMethod] =
      useState<ExpensePaymentMethod>('Cash');
    const [fundId, setFundId] = useState<number | null>(null);
    const [reference, setReference] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    const fundsQuery = useQuery({
      queryKey: ['operating-funds', branchDomain, refreshKey],
      enabled: Boolean(branchDomain),
      queryFn: async () => {
        const { data } = await axiosInstance.get<BranchFunds>(
          `/api/v1/${branchDomain}/operating-expenses/funds`
        );
        return data;
      }
    });
    const funds = fundsQuery.data?.funds ?? [];

    useEffect(() => {
      if (!expense?.workplace || !expense.category || !expense.paymentMethod) {
        setExpenseDate(todayInput());
        setWorkplace('Store');
        setCategory('Rent');
        setPayee('');
        setDescription('');
        setAmount('');
        setPaymentMethod('Cash');
        setFundId(null);
        setReference('');
        return;
      }

      setExpenseDate(expense.expenseDate.substring(0, 10));
      setWorkplace(expense.workplace);
      setCategory(expense.category);
      setPayee(expense.payee);
      setDescription(expense.description);
      setAmount(String(expense.amount));
      setPaymentMethod(expense.paymentMethod);
      setFundId(expense.fundId ?? null);
      setReference(expense.reference ?? '');
    }, [expense, openToken]);

    const submitForm = async () => {
      if (isSaving) return false;

      const parsedAmount = Number(amount);
      if (!expenseDate) {
        toast.error('Date spent is required.', { ...toastConfig, autoClose: 3000 });
        return false;
      }
      if (!payee.trim() || !description.trim()) {
        toast.error('Payee and description are required.', {
          ...toastConfig,
          autoClose: 3000
        });
        return false;
      }
      if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
        toast.error('Amount must be greater than zero.', {
          ...toastConfig,
          autoClose: 3000
        });
        return false;
      }
      if (paymentMethod === 'Fund' && !fundId) {
        toast.error('Choose a fund.', { ...toastConfig, autoClose: 3000 });
        return false;
      }

      const payload = {
        expenseDate,
        workplace,
        category,
        payee: payee.trim(),
        description: description.trim(),
        amount: parsedAmount,
        paymentMethod,
        fundId: paymentMethod === 'Fund' ? fundId : null,
        reference: reference.trim() || null
      };

      try {
        setIsSaving(true);
        if (expense) {
          await axiosInstance.put(
            `/api/v1/${branchDomain}/operating-expenses/${expense.operatingExpenseId}`,
            payload
          );
          toast.success('Expense updated.', { ...toastConfig, autoClose: 2000 });
        } else {
          await axiosInstance.post(
            `/api/v1/${branchDomain}/operating-expenses`,
            payload
          );
          toast.success('Expense saved.', { ...toastConfig, autoClose: 2000 });
        }
        onSaved();
        return true;
      } catch (error) {
        toast.error(apiError(error, 'Could not save the expense.'), {
          ...toastConfig,
          autoClose: 3000
        });
        return false;
      } finally {
        setIsSaving(false);
      }
    };

    useImperativeHandle(ref, () => ({ submitForm }));

    return (
      <Form>
        <Row className="g-3">
          <Col md={6}>
            <Form.Label>Date spent</Form.Label>
            <Form.Control
              type="date"
              value={expenseDate}
              onChange={event => setExpenseDate(event.target.value)}
            />
          </Col>
          <Col md={6}>
            <Form.Label>Store or shop</Form.Label>
            <Form.Select
              value={workplace}
              onChange={event =>
                setWorkplace(event.target.value as ExpenseWorkplace)
              }
            >
              {EXPENSE_WORKPLACES.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col md={6}>
            <Form.Label>Category</Form.Label>
            <Form.Select
              value={category}
              onChange={event =>
                setCategory(event.target.value as ExpenseCategory)
              }
            >
              {EXPENSE_CATEGORIES.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col md={6}>
            <Form.Label>Amount</Form.Label>
            <Form.Control
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={event => setAmount(event.target.value)}
            />
          </Col>
          <Col md={6}>
            <Form.Label>Payee</Form.Label>
            <Form.Control
              value={payee}
              maxLength={200}
              onChange={event => setPayee(event.target.value)}
            />
          </Col>
          <Col md={6}>
            <Form.Label>How it was paid</Form.Label>
            <Form.Select
              value={paymentValue(paymentMethod, fundId)}
              onChange={event => {
                const value = event.target.value;
                if (value.startsWith('fund:')) {
                  setPaymentMethod('Fund');
                  setFundId(Number(value.slice(5)));
                  return;
                }
                setPaymentMethod(value as ExpensePaymentMethod);
                setFundId(null);
              }}
            >
              <optgroup label="Payment">
                {EXPENSE_PAYMENT_METHODS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </optgroup>
              {funds.length > 0 ? (
                <optgroup label="Funds">
                  {funds.map(fund => (
                    <option key={fund.fundId} value={`fund:${fund.fundId}`}>
                      {fund.name}
                    </option>
                  ))}
                </optgroup>
              ) : null}
            </Form.Select>
            {paymentMethod === 'Fund' ? (
              <div className="text-body-tertiary fs-9 mt-1">
                This amount is taken out of the{' '}
                {funds.find(fund => fund.fundId === fundId)?.name ?? 'fund'}.
              </div>
            ) : null}
          </Col>
          <Col md={12}>
            <Form.Label>Description</Form.Label>
            <Form.Control
              value={description}
              maxLength={500}
              onChange={event => setDescription(event.target.value)}
            />
          </Col>
          <Col md={12}>
            <Form.Label>Reference</Form.Label>
            <Form.Control
              value={reference}
              maxLength={100}
              placeholder="Receipt, check, or transfer number"
              onChange={event => setReference(event.target.value)}
            />
          </Col>
        </Row>
      </Form>
    );
  }
);

export default ExpenseForm;
